import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Star, Crown, Check, Network, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONES = { basico: Building2, profissional: Star, enterprise: Crown };

const RECURSOS = ["Documentos e alvarás ilimitados", "Itens calibráveis e verificações diárias", "Alertas por e-mail e WhatsApp", "Relatórios em PDF para fiscalização", "Auditoria completa de ações"];

const precoComRede = (preco, pct) => (preco * (1 - pct / 100)).toFixed(2);

export default function Planos() {
  const [dados, setDados] = useState(null);
  const [ciclo, setCiclo] = useState("annual");
  const [erro, setErro] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/planos/publico`)
      .then((r) => r.json())
      .then(setDados)
      .catch(() => setErro(true));
  }, []);

  if (erro) return <div className="min-h-screen flex items-center justify-center text-slate-600" data-testid="planos-erro">Não foi possível carregar os planos.</div>;
  if (!dados) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="planos-loading">Carregando planos...</div>;

  const cicloAtual = dados.ciclos.find((c) => c.id === ciclo);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800" data-testid="planos-page">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-teal-700" style={{ fontFamily: "Manrope" }} data-testid="planos-logo">
            <ShieldCheck className="w-6 h-6" /> Conforme a Resolução
          </Link>
          <Link to="/login" data-testid="planos-login-link"><Button variant="outline" size="sm">Já sou cliente</Button></Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14 space-y-16">
        <section className="max-w-3xl space-y-4">
          <p className="text-xs font-semibold tracking-widest uppercase text-teal-700">Planos e preços</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>Conformidade regulatória sem dor de cabeça, por CNPJ.</h1>
          <p className="text-base md:text-lg text-slate-600">Cada farmácia assina individualmente. Redes com matriz e filiais ganham desconto progressivo automático, sem precisar pedir.</p>
        </section>

        <section className="space-y-6">
          <div className="flex flex-wrap gap-2" data-testid="planos-ciclos">
            {dados.ciclos.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCiclo(c.id)}
                data-testid={`planos-ciclo-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${ciclo === c.id ? "bg-teal-700 text-white border-teal-700" : "bg-white border-slate-200 hover:border-teal-400"}`}
              >
                {c.label.replace(/\s*\(.*\)/, "")}{c.desconto_percentual > 0 && <span className="ml-1 text-xs opacity-80">-{c.desconto_percentual}%</span>}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {dados.planos.map((plano) => {
              const Icon = ICONES[plano.id] || Building2;
              const preco = plano.precos[ciclo];
              const destaque = plano.id === "profissional";
              return (
                <div key={plano.id} className={`relative rounded-2xl border bg-white p-7 flex flex-col ${destaque ? "border-teal-600 shadow-lg shadow-teal-100" : "border-slate-200"}`} data-testid={`plano-card-${plano.id}`}>
                  {destaque && <Badge className="absolute -top-3 left-6 bg-teal-700">Mais escolhido</Badge>}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>{plano.nome}</h2>
                      <p className="text-xs text-slate-500">Até {plano.max_usuarios} usuários</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold tracking-tight" data-testid={`plano-preco-${plano.id}`}>R$ {preco.preco_mensal.toFixed(2)}</span>
                      <span className="text-slate-500 text-sm">/mês</span>
                    </div>
                    {preco.meses > 1 && <p className="text-xs text-slate-500 mt-1">R$ {preco.preco_total.toFixed(2)} cobrados a cada {preco.meses} meses</p>}
                  </div>
                  <ul className="space-y-2 text-sm flex-1">
                    {RECURSOS.map((r) => <li key={r} className="flex items-start gap-2"><Check className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />{r}</li>)}
                  </ul>
                  <Link to={`/checkout?plano=${plano.id}&ciclo=${ciclo}`} className="mt-7" data-testid={`plano-assinar-${plano.id}`}>
                    <Button className={`w-full ${destaque ? "bg-teal-700 hover:bg-teal-800" : ""}`} variant={destaque ? "default" : "outline"}>
                      Assinar {plano.nome} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-3xl bg-teal-900 text-white p-8 md:p-12 grid lg:grid-cols-[1.1fr_1fr] gap-10" data-testid="planos-desconto-rede">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-teal-200"><Network className="w-4 h-4" /> Matriz e filiais</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>Tem mais de um CNPJ? O desconto é automático.</h2>
            <p className="text-teal-100 text-sm md:text-base">Reconhecemos a rede pela raiz do CNPJ (os 8 primeiros dígitos). A matriz paga o preço do plano; cada filial cadastrada recebe um desconto maior, somado ao desconto do ciclo escolhido.</p>
            <p className="text-xs text-teal-200">Exemplo no plano {dados.planos[1]?.nome} {cicloAtual?.label.replace(/\s*\(.*\)/, "").toLowerCase()}: a 3ª farmácia da rede paga R$ {precoComRede(dados.planos[1]?.precos[ciclo].preco_mensal, 25)}/mês em vez de R$ {dados.planos[1]?.precos[ciclo].preco_mensal.toFixed(2)}.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 content-start">
            {dados.desconto_rede.map((linha, idx) => (
              <div key={linha.posicao} className="rounded-2xl bg-white/10 backdrop-blur p-5" data-testid={`rede-faixa-${idx}`}>
                <p className="text-xs text-teal-200">{linha.posicao}</p>
                <p className="text-3xl font-bold" style={{ fontFamily: "Manrope" }}>{linha.desconto_percentual === 0 ? "Preço cheio" : `-${linha.desconto_percentual}%`}</p>
              </div>
            ))}
          </div>
        </section>

        <footer className="text-xs text-slate-500 flex flex-wrap gap-4 justify-between border-t pt-6">
          <span>MSI Teixeira Coelho Tecnologia LTDA</span>
          <Link to="/termos" className="hover:text-teal-700">Termos e privacidade</Link>
        </footer>
      </main>
    </div>
  );
}
